import { notifMeta, type NotifCategoryKey } from './notification-meta';
import { BOARD_V1_ENABLED } from './layout-shell';

/**
 * 알림 → 갈 곳 (2026-08-18).
 *
 * 요구: "알림창에서 해당 알림을 클릭하면 알림으로 이동하도록 한다."
 *
 * 서버가 주는 단서는 셋뿐이다 — `type`(점 네임스페이스) · `source`(만든 곳) · `sourceId`.
 * 목적지 표를 여기 한 곳에 두고, 팝업은 결과만 쓴다(순수 함수라 검사도 쉽다).
 *
 * ## 어디까지 데려다줄 수 있나
 *
 * 화면마다 **딥링크가 있는 곳과 없는 곳**이 다르다. 있는 것만 정확히 쓰고, 없는 곳은
 * 그 화면까지만 데려간다 — 틀린 링크로 엉뚱한 글을 여는 것보다 낫다.
 *
 *   · 채팅  — `sourceId` 가 **방 번호**다. 방까지 연다(store 에 활성 방을 넣고 /chat).
 *   · 업무  — `source: 'task_project'` 일 때만 `sourceId` 가 **프로젝트 번호**다
 *             (`task.comment.added` 는 taskId 라 프로젝트로 쓰면 엉뚱한 곳이 열린다).
 *   · 일정·게시판·메일·예약 — 페이지에 딥링크가 없다(쿼리로 글/일정을 못 연다) → 화면까지만.
 *   · 결재  — **라우트 자체가 미등록**이다(메뉴에서 뺀 상태). 그래서 갈 곳이 없다.
 *   · 시스템 — 목적지가 없다.
 *
 * 갈 곳이 없으면 `null` 을 준다. 호출부는 그때 **읽음 처리만** 하고 팝업을 닫지 않는다 —
 * 눌렀는데 아무 일도 없이 팝업이 사라지면 고장으로 보인다.
 */

export interface NotifRoute {
  /** react-router 로 넘길 경로. */
  to: string;
  /** 채팅일 때만 — 이 방을 활성으로 만든 뒤 이동한다. */
  chatRoomId?: string;
}

interface RoutableNotification {
  type: string;
  source?: string;
  sourceId?: string | null;
}

export interface NotifRouteOptions {
  /**
   * 게시판 v1 화면별 주소를 쓰는가 (U6 · 2026-09-15). 안 주면 깃발 `VITE_BOARD_V1` 을 따른다.
   * 꺼져 있으면 게시판 알림은 종전처럼 `/board` 까지만 간다(깃발 뒤 · 회귀 없음).
   */
  boardV1?: boolean;
}

/**
 * 게시판 v1 — 알림이 가리키는 **글 번호**가 확실한 것만 글 주소로 보낸다 (U6 · 2026-09-15 · 서버 현실 기준).
 *
 *   · 새 글 알림 `board.post.created` — 서버가 지금 `source: 'board'` 로 만든다(설계는 `board_post`). **둘 다** 받는다.
 *   · 글 멘션 `board.post.mentioned` · 글 좋아요 `board.post.liked` — `source: 'board_post'` · `sourceId` = 글 번호.
 *   · 댓글 알림(`board_comment` · `board.comment.*`)의 `sourceId` 는 **댓글 번호**라 글로 못 간다 — 서버 리졸버(R12 계열) 뒤.
 *   · 공지 알림(`system.notice` · `board.notice.*`)은 공지 합치기(R2) 전이라 종전 경로(`/board`) 그대로.
 * 볼 수 없는 글이면 글 화면이 「이 글을 볼 수 없습니다」를 그린다(U3) — 알림이 404 로 끝나지 않는다.
 * ★ 위 머리 주석의 「게시판 — 딥링크가 없다」는 깃발 꺼짐(종전) 기준이다.
 */
const BOARD_POST_SOURCES = new Set(['board', 'board_post']);
const BOARD_POST_TYPES = new Set(['board.post.created', 'board.post.mentioned', 'board.post.liked']);

function boardV1PostRoute(n: RoutableNotification): NotifRoute | null {
  const id = n.sourceId ?? '';
  if (!/^[0-9]+$/.test(id)) return null;
  if (!BOARD_POST_TYPES.has(n.type) || !BOARD_POST_SOURCES.has(n.source ?? '')) return null;
  return { to: `/board/post/${id}` };
}

/** 카테고리 → 화면. 딥링크가 없는 곳은 화면 경로만, 갈 곳이 없으면 null. */
const CATEGORY_ROUTE: Readonly<Record<NotifCategoryKey, string | null>> = {
  'notif.cat.task': '/task',
  'notif.cat.calendar': '/calendar',
  'notif.cat.reservation': '/reservation/mine',
  'notif.cat.approval': null,   // 라우트 미등록(사용자 결정)
  'notif.cat.board': '/board',
  'notif.cat.chat': '/chat',
  'notif.cat.mail': '/mail',
  // 근태 — 촉진 통보를 받은 사람이 갈 곳은 **자기 휴가 현황**이다(관리 화면이 아니다).
  'notif.cat.attendance': '/erp/attendance/me/leave',
  'notif.cat.system': null,
};

export function notifRoute(n: RoutableNotification, opts: NotifRouteOptions = {}): NotifRoute | null {
  const category = notifMeta(n.type, n.source ?? '').categoryKey;
  const base = CATEGORY_ROUTE[category];
  if (!base) return null;

  const id = n.sourceId ?? '';

  // 채팅방은 경로가 아니라 store 로 고른다(/chat/:id 라우트가 없다).
  if (category === 'notif.cat.chat') {
    return n.source === 'chat' && id ? { to: base, chatRoomId: id } : { to: base };
  }

  // 업무는 프로젝트 딥링크가 있다 — **프로젝트 번호일 때만** 붙인다.
  if (category === 'notif.cat.task') {
    return n.source === 'task_project' && id ? { to: `${base}?project=${encodeURIComponent(id)}` } : { to: base };
  }

  // 게시판 v1(깃발 켜짐) — 글 번호가 확실한 알림만 글 주소로. 그 밖(댓글 · 공지 · 번호 없음)은 종전 화면까지만.
  if (category === 'notif.cat.board' && (opts.boardV1 ?? BOARD_V1_ENABLED)) {
    return boardV1PostRoute(n) ?? { to: base };
  }

  return { to: base };
}
